import { memo, useEffect, useState } from "react";

import { motion } from "framer-motion";
import {
  BarDatum,
  BarItemProps,
  ComputedBarDatum,
  ResponsiveBar,
} from "@nivo/bar";
import { BasicTooltipProps, useTooltip } from "@nivo/tooltip";

import { DEFAULT_CURRENCY_SYMBOL } from "@zenith/data/constants";
import { getSuffixedNumber } from "@zenith/utils";
import { getChartTheme } from "@zenith/utils/Helpers";
import { PaymentMethods } from "./enum";

/**
 * Props for Bar Graphic Component.
 * @interface
 * @name BarGraphicComponentProps
 * @kind interface
 * @exports
 */
export interface BarGraphicComponentProps {
  /**
   * Array of data.
   * @default []
   * @property
   * @name data
   * @kind property
   * @memberof BarGraphicComponentProps
   * @type {BarDatum[]}
   */
  data: BarDatum[];
  /**
   * Indexes the graphic by another property
   * (e.g: paymentMethod).
   * @property
   * @name indexBy
   * @kind property
   * @memberof BarGraphicComponentProps
   * @type {string}
   */
  indexBy: string;
  /**
   * Array of properties used
   * for displaying the graphic.
   * @property
   * @name keys
   * @kind property
   * @memberof BarGraphicComponentProps
   * @type {string[]}
   */
  keys: string[];
  /**
   * Accessibility HTML Attribute
   * (used for structuring the graphic).
   * @property
   * @name ariaLabel
   * @kind property
   * @memberof BarGraphicComponentProps
   * @type {string}
   */
  ariaLabel: string;
}

/**
 * Custom Computed Bar Datum Type.
 * @typedef
 * @name CustomComputedBarDatum
 * @kind variable
 */
type CustomComputedBarDatum =
  | (ComputedBarDatum<BarDatum> & { data: { value: number } })
  | undefined;

/**
 * Props for Custom Bar Component.
 * @interface
 * @name CustomBarComponentProps
 * @kind interface
 */
interface CustomBarComponentProps extends BarItemProps<BarDatum> {
  /**
   * The selected Bar.
   * @method
   * @name selectedBar
   * @kind property
   * @memberof CustomBarComponentProps
   */
  selectedBar: CustomComputedBarDatum;
  /**
   * The selected Bar setter.
   * @method
   * @name setSelectedBar
   * @kind property
   * @memberof CustomBarComponentProps
   * @param {CustomComputedBarDatum} bar The selected bar data.
   * @returns {void}
   */
  setSelectedBar: (bar: CustomComputedBarDatum) => void;
  /**
   * The animate prop setter.
   * @property
   * @name setAnimate
   * @kind property
   * @memberof CustomBarComponentProps
   * @type {React.Dispatch<React.SetStateAction<boolean>>}
   */
  setAnimate: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Function that cleares the timeout.
   * @method
   * @name clearTimeoutFn
   * @kind property
   * @memberof CustomBarComponentProps
   * @param {NodeJS.Timeout=} id (optional)
   * If exists, it clears the timeout with the id.
   * @returns {void}
   */
  clearTimeoutFn: (id?: NodeJS.Timeout) => void;
  /**
   * If `true`, the graphic will be animated.
   * @property
   * @name animate
   * @kind property
   * @memberof CustomBarComponentProps
   * @type {boolean}
   */
  animate: boolean;
}

/**
 * Props for Custom Bar ToolTip.
 * @interface
 * @name CustomBarToolTipProps
 * @kind interface
 */
interface CustomBarToolTipProps extends BasicTooltipProps {
  /**
   * Data dictionary, neccesary for the bar graphic.
   * @property
   * @name data
   * @kind property
   * @memberof CustomBarToolTipProps
   * @type {BarDatum}
   */
  data: BarDatum;
}

const theme = getChartTheme();

/**
 * Memoized Custom Bar Component.
 * @constant
 * @name CustomBarComponent
 * @kind variable
 * @type {React.FC<CustomBarComponentProps>}
 */
const CustomBarComponent: React.FC<CustomBarComponentProps> = memo(
  /**
   * Custom Bar Component.
   * @function
   * @name CustomBarComponent
   * @kind function
   * @memberof CustomBarComponent
   * @param {CustomBarComponentProps} props Props for Custom Bar Component.
   * @returns {JSX.Element} Custom Bar Component.
   */
  function CustomBarComponent({
    bar,
    selectedBar,
    setAnimate,
    animate,
    clearTimeoutFn,
  }: CustomBarComponentProps): JSX.Element {
    const { showTooltipAt, hideTooltip } = useTooltip();
    const BAR_MAX_WIDTH = 32.57;
    const w = bar.width > BAR_MAX_WIDTH ? BAR_MAX_WIDTH : bar.width; // makes sure the width is never bigger than the max one
    const x = bar.x + bar.width / 2 - w / 2; // sets the bar horizontal position on the graphic

    // sets the tooltip on top of the bar being passed as parameter
    const setToolTipAt = (bar: ComputedBarDatum<BarDatum> | undefined) => {
      if (!bar || !bar.data || !bar.data.value) return;
      showTooltipAt(
        <CustomToolTip
          id={bar.data.index}
          value={bar.data.value}
          data={bar.data.data}
        />,
        [bar.absX + bar.width / 2, bar.absY]
      );
    };

    if (!selectedBar) {
      clearTimeoutFn();
    }

    const barColor =
      selectedBar && selectedBar.index === bar.index
        ? "bg-primary-300"
        : "bg-pink-150";
    const [background, setBackground] = useState<string>(barColor);

    const handleOnMouseEnter = () => {
      setBackground("bg-primary-300");
      setToolTipAt(bar);
    };

    const handleOnMouseLeave = () => {
      setBackground("bg-pink-150");
      hideTooltip();
    };

    return (
      <svg>
        <foreignObject x={x} y={bar.y} width={w} height={bar.height}>
          <motion.div
            style={{ height: animate ? "100%" : "0%" }}
            animate={{ height: "100%" }}
            transition={{ duration: 0.5 }}
            onAnimationComplete={() => setAnimate(false)}
            className={`w-full cursor-pointer rounded-lg outline-none ${background}`}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          ></motion.div>
        </foreignObject>
      </svg>
    );
  }
);

/**
 * Memoized Custom Tool Tip Component.
 * @constant
 * @name CustomToolTip
 * @kind variable
 * @type {React.NamedExoticComponent<CustomBarToolTipProps>}
 */
const CustomToolTip: React.NamedExoticComponent<CustomBarToolTipProps> = memo(
  /**
   * Custom Tool Tip Component.
   * @function
   * @name CustomToolTip
   * @kind function
   * @memberof CustomToolTip
   * @param {CustomBarToolTipProps} bar The graphic bar data.
   * @returns {JSX.Element} The graphic bar with the Custom Tool Tip Component.
   */
  function CustomToolTip(bar: CustomBarToolTipProps): JSX.Element {
    return (
      <div>
        <div className="absolute -left-10 -top-8 z-10 flex h-6 items-center justify-center rounded-lg bg-black px-3 text-xs text-white">
          <div className="mr-1">{`${DEFAULT_CURRENCY_SYMBOL}`}</div>
          <div> {`${getSuffixedNumber(bar.value as number)}`}</div>
          <div className="ml-2">{`${bar.data.quantity}`}</div>
        </div>
        <div className="z-1 absolute -top-3 left-1/2 -translate-x-1/2 transform">
          <div className="h-2 w-2 rotate-45 transform bg-black"></div>
        </div>
      </div>
    );
  }
);

/**
 * We cannot break lines with SVG. This is a work around with Tspan.
 * for more info, please check {@link https://github.com/plouc/nivo/issues/353 | this issue}.
 * @function
 * @name getTspanGroups
 * @kind function
 * @param {keyof typeof PaymentMethods} value Bar key value.
 * @param {number=} maxLines Number of lines to break the label into.
 * @returns {JSX.Element[] | undefined} Bar axisBottom label.
 */
function getTspanGroups(
  value: keyof typeof PaymentMethods,
  maxLines = 2
): JSX.Element[] | undefined {
  if (!Object.keys(PaymentMethods).includes(value)) return;

  const words = PaymentMethods[value].replace(/_/g, " ").split(" ");

  //gets the number of letters before there's a space
  const maxLineLength = value.replace(/_/g, " ").indexOf(" ");

  type linesAcc = {
    lines: string[];
    currLine: string;
  };

  //reduces the words into lines of maxLineLength
  const assembleLines: linesAcc = words.reduce(
    (acc: linesAcc, word: string) => {
      //if the current line isn't empty and the word + current line is larger than the allowed line size, create a new line and update current line
      if ((word + acc.currLine).length > maxLineLength && acc.currLine !== "") {
        return {
          lines: acc.lines.concat([acc.currLine]),
          currLine: word,
        };
      }
      //otherwise add the word to the current line
      return {
        ...acc,
        currLine: acc.currLine + " " + word,
      };
    },
    { lines: [], currLine: "" }
  );

  //add the ending state of current line (the last line) to lines
  const allLines = assembleLines.lines.concat([assembleLines.currLine]);

  //for now, only take first 2 lines due to tick spacing and possible overflow
  const lines = allLines.slice(0, maxLines);
  const children: JSX.Element[] = [];
  let dy = 0;

  lines.forEach((lineText, i) => {
    children.push(
      <tspan x={0} dy={dy} key={i} fill="#9B9B9B">
        {
          // if on the second line, and that line's length is within 3 of the max length, add ellipsis
          1 === i && allLines.length > 2
            ? lineText.slice(0, maxLineLength - 3) + "..."
            : lineText
        }
      </tspan>
    );
    //increment dy to render next line text below
    dy += 15;
  });

  return children;
}

/**
 * Memoized Bar Graphic Component.
 * @constant
 * @name BarGraphicComponent
 * @kind variable
 * @type {React.NamedExoticComponent<BarGraphicComponentProps>}
 */
const BarGraphicComponent: React.NamedExoticComponent<BarGraphicComponentProps> =
  memo(
    /**
     * Bar Graphic Component.
     * @function
     * @name BarGraphicComponent
     * @kind function
     * @memberof BarGraphicComponent
     * @param {BarGraphicComponentProps} props Props for Bar Graphic Component.
     * @returns {JSX.Element} Bar Graphic Component.
     */
    function BarGraphicComponent(props: BarGraphicComponentProps): JSX.Element {
      const [selectedBar, setSelectedBar] = useState<
        ComputedBarDatum<BarDatum> & { data: { value: number } }
      >();
      const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | undefined>();
      const [animate, setAnimate] = useState(false);

      const clearTimeoutFn = (id?: NodeJS.Timeout) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          setTimeoutId(undefined);
        }
        if (id !== undefined) {
          setTimeoutId(id);
        }
      };

      useEffect(() => {
        if (props.data.length > 0) {
          setAnimate(true);
        }
      }, [props.data]);

      return (
        <ResponsiveBar
          data={props.data}
          keys={props.keys}
          enableLabel={false}
          indexBy={props.indexBy}
          margin={{ top: 50, right: 0, bottom: 50, left: 50 }}
          borderRadius={10}
          axisTop={null}
          colors={["#F9DCD9"]}
          axisRight={null}
          theme={theme}
          axisLeft={{
            tickSize: 0,
            tickPadding: 10,
            ticksPosition: "before",
            format: function (value) {
              return getSuffixedNumber(value);
            },
          }}
          axisBottom={{
            tickSize: 0,
            tickPadding: 20,
            renderTick: ({
              opacity,
              textAnchor,
              textX,
              textY,
              value,
              x,
              y,
            }) => {
              return (
                <g transform={`translate(${x},${y})`} style={{ opacity }}>
                  <text
                    style={{ fontSize: "12px" }}
                    textAnchor="start"
                    transform={`translate(${textX},${textY})`}
                  >
                    {getTspanGroups(value)}
                  </text>
                </g>
              );
            },
          }}
          enableGridX={false}
          enableGridY={false}
          markers={[
            {
              axis: "y",
              value: 0,
              lineStyle: {
                stroke: "#C6C6C6",
                strokeWidth: "0.5",
              },
            },
          ]}
          role="application"
          ariaLabel={props.ariaLabel}
          barComponent={(ele: BarItemProps<BarDatum>) => (
            <CustomBarComponent
              {...ele}
              selectedBar={selectedBar}
              setSelectedBar={setSelectedBar}
              setAnimate={setAnimate}
              animate={animate}
              clearTimeoutFn={clearTimeoutFn}
            />
          )}
          isFocusable={false}
        />
      );
    }
  );

export default BarGraphicComponent;
